import type {
  Organization,
  PaginatedList,
  Patrol,
  Person,
} from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useHistoryState } from '../../hooks/use-history-state.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { ErrorPage } from '../error/index.js';
import { DisplayPersonView } from './display.view.js';
import { ExistingPersonPage } from './existing-person.js';

export const DisplayPersonContent: FC<ExistingEntityViewProps<Person.View>> = ({
  data: person,
  id,
  refresh,
}) => {
  const navigate = useNavigate();
  const auth = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();

  const [organization] = useAxios<Organization.Type>('/api/v1/my/organization');

  const [recentPatrolPagination, setRecentPatrolPagination] = useHistoryState(
    'recentPatrolPagination',
    {
      limit: 12,
      skip: 0,
    },
  );
  const [recentPatrols] = useAxios<PaginatedList<Patrol.Type>>(
    `/api/v1/person/${encodeURIComponent(id)}/patrols`,
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useMemo(
      () => ({
        params: {
          ...recentPatrolPagination,
          filter: JSON.stringify({
            cancelled: null,
            [`patrollers.${encodeURIComponent(id)}.declined`]: null,
            [`patrollers.${encodeURIComponent(id)}.removed`]: null,
            when: { $lte: DateTime.now() },
          }),
          sort: '-when',
        },
      }),
      [recentPatrolPagination],
    ),
  );

  const [scheduledPatrols] = useAxios<PaginatedList<Patrol.Type>>(
    `/api/v1/person/${encodeURIComponent(id)}/patrols`,
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useMemo(
      () => ({
        params: {
          filter: JSON.stringify({
            cancelled: null,
            [`patrollers.${encodeURIComponent(id)}.declined`]: null,
            [`patrollers.${encodeURIComponent(id)}.removed`]: null,
            when: { $gt: DateTime.now() },
          }),
          limit: 12,
          sort: '-when',
        },
      }),
      [],
    ),
  );

  const [busy, setBusy] = useState(false);
  const changeFinished = (action: 'finish' | 'resume'): void => {
    setBusy(true);
    auth.axios
      .request({
        method: 'POST',
        url: `/api/v1/person/${encodeURIComponent(id)}/${action}`,
      })
      .then(() => {
        refresh();
        return undefined;
      })
      .catch((error: AxiosError) => {
        console.error(error);
        Sentry.captureException(error);
        enqueueSnackbar(
          action === 'finish'
            ? 'Error finishing person'
            : 'Error resuming person',
          { variant: 'error' },
        );
      })
      .finally(() => setBusy(false));
  };
  const handleFinish = (): void => changeFinished('finish');
  const handleResume = (): void => changeFinished('resume');

  if (recentPatrols.loading || scheduledPatrols.loading) {
    return <MiddleSpinner />;
  }
  if (recentPatrols.error) return <ErrorPage error={recentPatrols.error} />;
  if (scheduledPatrols.error) {
    return <ErrorPage error={scheduledPatrols.error} />;
  }

  return (
    <DisplayPersonView
      busy={busy}
      data={person}
      edit={() => navigate(`/person/${encodeURIComponent(id)}/edit`)}
      handleFinish={handleFinish}
      handleResume={handleResume}
      hideUnstartedBadge={organization.data?.hideUnstartedPatrolsBadge}
      id={id}
      recentPatrolPagination={recentPatrolPagination}
      recentPatrols={recentPatrols.data}
      scheduledPatrols={scheduledPatrols.data}
      setRecentPatrolPagination={setRecentPatrolPagination}
    />
  );
};

export const DisplayPersonPage: FC = () => (
  <ExistingPersonPage view={DisplayPersonContent} />
);
